import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Images } from "../store/Constants";
import "../assets/css/style.css";
import { useDarkMode } from "../contexts/DarkModeContext";
import { useSidebar } from "../contexts/SidebarContext";

const Navbar = () => {
  const { isCollapsed, toggleCollapse } = useSidebar();
  const { User } = JSON.parse(sessionStorage.getItem("data"));
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState({});
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem("data");
    navigate("/login");
  };

  const restrictedRoutes = [
    "/chat",
    "/live",
    "/go-live",
    "/settings/qod-categories",
    "/settings/select-sub-qod",
    "/settings/interest-categories",
    "/settings/select-sub-interest",
    "/pending-podcast",
  ];
  const isRestrictedRoute = restrictedRoutes.includes(location.pathname);
  const data = JSON.parse(sessionStorage.getItem("data"));
  useEffect(() => {
    if (isRestrictedRoute && !isCollapsed) {
      toggleCollapse();
    }
  }, [isRestrictedRoute, isCollapsed, toggleCollapse]);

  const handleSearch = async (e) => {
    const keyword = e.target.value;
    setSearchTerm(keyword);

    if (keyword.trim() === "") {
      setSearchResults({});
      return;
    }

    try {
      setLoading(true);

      // Fetch user, podcast, debate, and petition results
      const mainSearchPromise = axios.get(`/users/search-main`, {
        params: { keyword },
        headers: { Authorization: `Bearer ${data?.token}` },
      });

      // Fetch TikTok search results with error handling
      let tiktokSearchResults = [];
      try {
        const tiktokSearchResponse = await axios.get(
          `/admin/tiktok/content/search`,
          {
            headers: { Authorization: `Bearer ${data?.token}` },
          }
        );
        tiktokSearchResults = tiktokSearchResponse.data.data;
      } catch (tiktokError) {
        console.error("Error fetching TikTok search results:", tiktokError);
        // Use an empty array for TikTok results in case of error
        tiktokSearchResults = [];
      }

      // Wait for main search results
      const mainSearchResponse = await mainSearchPromise;

      setSearchResults({
        ...mainSearchResponse.data.data, // Existing data (users, podcasts, etc.)
        tiktokVideos: tiktokSearchResults, // Add TikTok results
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching search results:", err);
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults({});
  };

  if (isRestrictedRoute) {
    return null;
  }

  const handleLiveDebate = async (debateData) => {
    clearSearch();
    if (debateData?.debatePath && debateData?.status === "Finished") {
      navigate("/debatede-details", { state: { debateData } });
      return;
    }

    if (
      debateData?.active === false ||
      !debateData ||
      debateData?.status === "Finished"
    ) {
      alert("Debate is ended.");
      return;
    }

    navigate(`/live`, { state: { debateData, role: "subscriber" } });
  };

  const formatNumber = (num) => {
    if (!num) return "0";

    if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    }
    if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (num >= 1_000) {
      return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }

    return num.toLocaleString();
  };

  return (
    <nav
      style={{ boxShadow: "none", borderBottom: "none" }}
      className={`navbar ${
        darkMode ? "dark-mode" : ""
      } navbar-expand px-3 no-box-shadow`}
    >
      <button
        onClick={toggleCollapse}
        className={`btn toggle-btn me-3 ${darkMode ? "btn-light" : ""}`}
        type="button"
      >
        <span
          className={`navbar-toggler-icon ${
            darkMode ? "toggler-icon-dark" : "toggler-icon-light"
          }`}
        ></span>
      </button>
      <Link className="navbar-brand" to="">
        <img src={Images.bluelogo} alt="" />
      </Link>
      <div
        className="navbar-menu-custom-class"
        style={{ position: "relative" }}
      >
        <img src={Images.searchIcon} alt="" />
        <input
          placeholder="Search...."
          value={searchTerm}
          onChange={handleSearch}
          style={{
            height: "50px",
            width: "100%",
            paddingLeft: "10px",
            outline: "none",
            border: "none",
            background: darkMode ? "rgb(51, 51, 51)" : "",
            color: darkMode ? "white" : "",
          }}
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: darkMode ? "white" : "black",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            ×
          </button>
        )}
      </div>
      <div className="dropdown profile-dropdown">
        <button
          className="btn profile-btn"
          type="button"
          style={{ boxShadow: "none" }}
          data-bs-toggle="dropdown"
        >
          <img src={User?.image} alt="" className="profile-img" />
        </button>
        <ul className={`dropdown-menu ${darkMode ? "dark-mode" : ""}`}>
          <li>
            <Link className="dropdown-item" to="/profile">
              View Profile
            </Link>
          </li>
          <li>
            <Link className="dropdown-item" to="/privacy">
              Privacy
            </Link>
          </li>
          <li>
            <Link className="dropdown-item" to="/settings">
              Setting
            </Link>
          </li>
          <li>
            <p
              className="dropdown-item"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
            >
              Logout
            </p>
          </li>
        </ul>
      </div>

      {/* Search Results Dropdown */}
      {searchTerm && (
        <div
          className="search-results"
          style={{
            position: "absolute",
            top: "100%",
            left: "0",
            right: "0",
            background: darkMode ? "#333" : "#fff",
            color: darkMode ? "#fff" : "#000",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            zIndex: "1000",
            padding: "10px",
            borderRadius: "8px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {searchResults.users?.length > 0 && (
                <div>
                  <h5>Users</h5>
                  {searchResults.users.map((user, index) => (
                    <p
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        clearSearch();
                        navigate("/profile", { state: { user: user } });
                      }}
                      key={index}
                    >
                      {user.name}
                    </p>
                  ))}
                </div>
              )}
              {searchResults.podcasts?.length > 0 && (
                <div>
                  <h5>Podcasts</h5>
                  {searchResults.podcasts.map((podcast, index) => (
                    <p
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        clearSearch();
                        navigate("/podcast-details", {
                          state: { podcast: podcast },
                        });
                      }}
                      key={index}
                    >
                      {podcast.topic}
                    </p>
                  ))}
                </div>
              )}
              {searchResults.debates?.length > 0 && (
                <div>
                  <h5>Debates</h5>
                  {searchResults.debates.map((debate, index) => (
                    <p
                      style={{ cursor: "pointer" }}
                      onClick={() => handleLiveDebate(debate)}
                      key={index}
                    >
                      {debate.topic}
                    </p>
                  ))}
                </div>
              )}
              {searchResults.petitions?.length > 0 && (
                <div>
                  <h5>Petitions</h5>
                  {searchResults.petitions.map((petition, index) => (
                    <p
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        clearSearch();
                        navigate("/sign-petition", {
                          state: { petitionID: petition?._id },
                        });
                      }}
                      key={index}
                    >
                      {petition.title}
                    </p>
                  ))}
                </div>
              )}
              {searchResults.tiktokVideos?.length > 0 && (
                <div>
                  <h5>TikTok Videos</h5>
                  {searchResults.tiktokVideos.map((video, index) => (
                    <div
                      key={index}
                      style={{ cursor: "pointer", marginBottom: "10px" }}
                      onClick={() => {
                        clearSearch();
                        navigate(`/tiktok-video/${video._id}`, {
                          state: { video },
                        });
                      }}
                    >
                      <p>{video.caption}</p>
                      <small className="d-flex">
                        {" "}
                        <span className="material-symbols-outlined">
                          visibility
                        </span>{" "}
                        &nbsp; {formatNumber(video.views?.[0])}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
